/**
 * FikoCAD Diff Dialog - CATIA V5 Compare Products Tarzı
 * Taşınabilir, ufak boyutlu dialog - FikoThreeJS ile iki viewport
 * Arka pencere (fiko-cad-diff.component) ile aynı açık gri tema
 */

import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  OnDestroy,
  AfterViewInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Injector,
  ViewContainerRef,
  EnvironmentInjector,
  NgZone,
  SimpleChanges,
  OnChanges,
  Type,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { CadDiffSonuc, RGB, CadDiffColors, DiffModelSecim } from './FikoCadDiffService';

export type PanelViewMode = 'diff_a' | 'diff_b' | 'diff_both' | 'original_a' | 'original_b';

@Component({
  selector: 'fikoyt-cad-diff-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="govde">
      <!-- Header -->
      <div class="baslik">
        <i class="exchange alternate icon"></i>
        CAD Farklar - Karşılaştırma
        <span class="alt-bilgi" *ngIf="sonuc">
          | {{ sonuc.sure?.toFixed(0) || 0 }}ms
        </span>
      </div>

      <!-- İki Viewport Yan Yana -->
      <div class="section-panel">
        <div class="section-title">Model Görünümü</div>
        <div class="viewport-row">
          <!-- Sol Panel - Model A -->
          <div class="viewport-panel">
            <div class="model-baslik">
              <span class="etiket a">A</span>
              <select class="panel-secici" [(ngModel)]="panelAMode" (ngModelChange)="onPanelModeChange('A')">
                <option value="diff_a">Analiz (Silinen)</option>
                <option value="diff_b">Analiz (Eklenen)</option>
                <option value="diff_both">Eklenen + Silinen</option>
                <option value="original_a">Orijinal Eski</option>
                <option value="original_b">Orijinal Yeni</option>
              </select>
            </div>
            <div class="viewer-container" #viewerContainerA>
              <ng-container #threeJsContainerA></ng-container>
              <div class="yukleniyor" *ngIf="!hazirA">
                <div class="spinner"></div>
                <span>Yükleniyor...</span>
              </div>
            </div>
          </div>

          <!-- Sağ Panel - Model B -->
          <div class="viewport-panel">
            <div class="model-baslik">
              <span class="etiket b">B</span>
              <select class="panel-secici" [(ngModel)]="panelBMode" (ngModelChange)="onPanelModeChange('B')">
                <option value="diff_b">Analiz (Eklenen)</option>
                <option value="diff_a">Analiz (Silinen)</option>
                <option value="diff_both">Eklenen + Silinen</option>
                <option value="original_b">Orijinal Yeni</option>
                <option value="original_a">Orijinal Eski</option>
              </select>
            </div>
            <div class="viewer-container" #viewerContainerB>
              <ng-container #threeJsContainerB></ng-container>
              <div class="yukleniyor" *ngIf="!hazirB">
                <div class="spinner"></div>
                <span>Yükleniyor...</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Renk Açıklaması -->
      <div class="renk-aciklama">
        <div class="renk-item">
          <span class="renk-kutu" [style.background]="rgbToHex(colors.same)"></span>
          <span>Aynı</span>
        </div>
        <div class="renk-item">
          <span class="renk-kutu" [style.background]="rgbToHex(colors.removed)"></span>
          <span>Silinen</span>
        </div>
        <div class="renk-item">
          <span class="renk-kutu" [style.background]="rgbToHex(colors.added)"></span>
          <span>Eklenen</span>
        </div>
      </div>

      <!-- İstatistikler -->
      <div class="catia-stats">
        <div class="stat-row">
          <span class="stat-label">Eşleşme:</span>
          <span class="stat-value yesil">{{ sonuc?.toleransIcindeYuzde?.toFixed(1) || 0 }}%</span>
        </div>
        <div class="stat-row">
          <span class="stat-label">Silinen nokta:</span>
          <span class="stat-value kirmizi">{{ formatSayi(sonuc?.farkliVertexA || 0) }}</span>
        </div>
        <div class="stat-row">
          <span class="stat-label">Eklenen nokta:</span>
          <span class="stat-value mavi">{{ formatSayi(sonuc?.farkliVertexB || 0) }}</span>
        </div>
        <div class="stat-row" *ngIf="sonuc?.maxSapma > 0">
          <span class="stat-label">Maks. sapma (P99):</span>
          <span class="stat-value">{{ formatMesafe(sonuc?.maxSapma || 0) }}</span>
        </div>
        <div class="stat-row" *ngIf="sonuc?.ortalamaSapma > 0">
          <span class="stat-label">Ort. sapma:</span>
          <span class="stat-value">{{ formatMesafe(sonuc?.ortalamaSapma || 0) }}</span>
        </div>
      </div>

      <!-- Araç Çubuğu -->
      <div class="arac-cubugu">
        <button class="ui icon mini button" (click)="kameraSifirla()" title="Kamerayı Sıfırla">
          <i class="undo icon"></i>
        </button>
        <button class="ui icon mini button" [class.active]="syncEnabled" (click)="toggleSync()" title="Senkronizasyon">
          <i class="sync icon"></i>
        </button>
        <button class="ui icon mini button" (click)="ekranaSigdir()" title="Ekrana Sığdır">
          <i class="expand icon"></i>
        </button>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: flex;
      width: 100%;
      height: 100%;
      min-width: 300px;
      min-height: 300px;
      font-size: 10px;
    }

    .govde {
      flex: 1;
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
      min-width: 300px;
      min-height: 300px;
      color: #333;
      background: linear-gradient(135deg, #f5f5f5, #e8e8e8);
      border: 1px solid #bbb;
      border-radius: 4px;
      padding: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      box-sizing: border-box;
      overflow: hidden;
    }

    .baslik {
      flex-shrink: 0;
      color: #1a5276;
      font-weight: 600;
      font-size: 12px;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      gap: 4px;
      padding-bottom: 6px;
      border-bottom: 1px solid #ccc;
    }

    .baslik i.icon { margin: 0; color: #2980b9; }

    .alt-bilgi {
      margin-left: auto;
      font-size: 9px;
      color: #666;
    }

    .section-panel {
      flex: 1;
      display: flex;
      flex-direction: column;
      background: #fff;
      border: 1px solid #ccc;
      border-radius: 3px;
      padding: 6px 8px;
      margin-bottom: 6px;
      min-height: 0;
      overflow: hidden;
    }

    .section-title {
      flex-shrink: 0;
      font-size: 10px;
      font-weight: 600;
      color: #1a5276;
      margin-bottom: 6px;
      padding-bottom: 4px;
      border-bottom: 1px solid #ddd;
    }

    .viewport-row {
      flex: 1;
      display: flex;
      gap: 6px;
      min-height: 0;
    }

    .viewport-panel {
      flex: 1;
      display: flex;
      flex-direction: column;
      background: #fafafa;
      border: 1px solid #ddd;
      border-radius: 3px;
      overflow: hidden;
      transition: opacity 0.2s ease;
      min-height: 0;
      min-width: 0;
    }

    .model-baslik {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 4px 6px;
      background: #f0f0f0;
      font-size: 9px;
      color: #666;
      border-bottom: 1px solid #ddd;
    }

    .panel-secici {
      flex: 1;
      padding: 2px 4px;
      font-size: 9px;
      border: 1px solid #ccc;
      border-radius: 3px;
      background: #fff;
      color: #333;
      cursor: pointer;
      outline: none;
    }

    .panel-secici:hover {
      border-color: #2980b9;
    }

    .panel-secici:focus {
      border-color: #2980b9;
      box-shadow: 0 0 3px rgba(41, 128, 185, 0.3);
    }

    .etiket {
      width: 16px;
      height: 16px;
      border-radius: 2px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: 10px;
      color: white;
    }

    .etiket.a { background: #3498db; }
    .etiket.b { background: #9b59b6; }

    .viewer-container {
      flex: 1;
      width: 100%;
      min-height: 100px;
      position: relative;
      background: #2a2a3a;
      overflow: hidden;
    }

    .yukleniyor {
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      background: rgba(42, 42, 58, 0.9);
      color: #888;
      font-size: 9px;
    }

    .spinner {
      width: 16px;
      height: 16px;
      border: 2px solid rgba(0,150,200,0.2);
      border-top-color: #2980b9;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    .renk-aciklama {
      flex-shrink: 0;
      display: flex;
      justify-content: center;
      gap: 20px;
      margin-bottom: 6px;
      padding: 4px 0;
    }

    .renk-item {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 9px;
      color: #666;
    }

    .renk-kutu {
      width: 20px;
      height: 12px;
      border-radius: 2px;
      border: 1px solid #999;
    }

    .catia-stats {
      flex-shrink: 0;
      background: #fff;
      border: 1px solid #ccc;
      border-radius: 3px;
      padding: 6px 8px;
      margin-bottom: 6px;
    }

    .stat-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 2px 0;
      font-size: 9px;
      border-bottom: 1px solid #f0f0f0;
    }

    .stat-row:last-child { border-bottom: none; }

    .stat-label { color: #666; }

    .stat-value {
      color: #333;
      font-weight: 500;
    }

    .stat-value.kirmizi { color: #d12b4a; }
    .stat-value.mavi { color: #2e85d0; }
    .stat-value.yesil { color: #27ae60; }

    .arac-cubugu {
      flex-shrink: 0;
      display: flex;
      gap: 4px;
      justify-content: center;
    }

    .arac-cubugu button {
      padding: 4px !important;
      opacity: 0.7;
    }

    .arac-cubugu button:hover { opacity: 1; }

    .arac-cubugu button.active {
      opacity: 1;
      background: #e8f4fc;
      box-shadow: 0 0 4px rgba(41, 128, 185, 0.5);
    }

    .ui.icon.mini.button {
      padding: 4px;
      margin: 0;
      background: #f0f0f0;
      border: 1px solid #ccc;
      color: #333;
    }

    .ui.icon.mini.button:hover {
      background: #e0e0e0;
    }

    .ui.icon.mini.button i.icon {
      margin: 0;
      font-size: 10px;
    }
  `]
})
export class FikoCadDiffDialogComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  // Dialog parametreleri (FikoDialogCore'dan gelir)
  @Input() pencere?: { instance: any; _refreshId: number };

  // Input parametreleri
  private _sonuc: CadDiffSonuc | null = null;
  @Input()
  set sonuc(value: CadDiffSonuc | null) {
    this._sonuc = value;
    // Viewport'lar hazırsa mesh'leri yükle
    if (value && this.viewportsReady) {
      this.loadMeshesWhenReady();
    }
  }
  get sonuc(): CadDiffSonuc | null {
    return this._sonuc;
  }
  @Input() modelA: DiffModelSecim | null = null;
  @Input() modelB: DiffModelSecim | null = null;
  @Input() colors: CadDiffColors = {
    same: { r: 0.78, g: 0.72, b: 0.32 },    // Warm gold (CATIA V5)
    removed: { r: 0.82, g: 0.18, b: 0.32 },  // Coral-magenta
    added: { r: 0.18, g: 0.52, b: 0.82 }     // Steel blue
  };
  @Input() edgeColor: RGB = { r: 0.1, g: 0.1, b: 0.1 };
  @Input() showEdges = true;

  // ViewChild referansları
  @ViewChild('threeJsContainerA', { read: ViewContainerRef, static: true })
  viewContainerRefA!: ViewContainerRef;

  @ViewChild('threeJsContainerB', { read: ViewContainerRef, static: true })
  viewContainerRefB!: ViewContainerRef;

  @ViewChild('viewerContainerA', { static: true })
  viewerContainerARef!: ElementRef<HTMLDivElement>;

  @ViewChild('viewerContainerB', { static: true })
  viewerContainerBRef!: ElementRef<HTMLDivElement>;

  // Three.js referansları
  private threeJsA?: any;
  private threeJsB?: any;
  private sahneA?: any;
  private sahneB?: any;
  private componentRefA?: any;
  private componentRefB?: any;

  // Panel mesh referansları
  private diffMeshA?: THREE.Mesh;
  private diffMeshB?: THREE.Mesh;
  private diffEdgesA?: THREE.LineSegments;
  private diffEdgesB?: THREE.LineSegments;
  private originalMeshA?: THREE.Mesh;
  private originalMeshB?: THREE.Mesh;
  private originalEdgesA?: THREE.LineSegments;
  private originalEdgesB?: THREE.LineSegments;

  hazirA = false;
  hazirB = false;
  syncEnabled = true;
  panelAMode: PanelViewMode = 'diff_a';
  panelBMode: PanelViewMode = 'diff_b';
  private isSyncing = false;
  private viewportsReady = false;
  private meshesLoaded = false;

  // ResizeObserver
  private resizeObserverA?: ResizeObserver;
  private resizeObserverB?: ResizeObserver;
  private resizeObserverMain?: ResizeObserver;

  constructor(
    private cd: ChangeDetectorRef,
    private injector: Injector,
    private environmentInjector: EnvironmentInjector,
    private ngZone: NgZone,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    // FikoDialogCore'un wrapper div'lerinin boyutlandırmasını düzelt
    this.setupParentStyles();
  }

  /**
   * FikoDialogCore içindeki parent wrapper'ların stillerini ayarla
   * Bu sayede dialog boyutlandığında içerik de boyutlanır
   */
  private setupParentStyles(): void {
    const hostElement = this.elementRef.nativeElement as HTMLElement;
    if (!hostElement) return;

    // Host element'i flex container yap
    hostElement.style.display = 'flex';
    hostElement.style.width = '100%';
    hostElement.style.height = '100%';
    hostElement.style.minWidth = '400px';
    hostElement.style.minHeight = '350px';

    // Parent wrapper (ngComponentOutlet'in oluşturduğu div)
    const parentWrapper = hostElement.parentElement;
    if (parentWrapper) {
      parentWrapper.style.display = 'flex';
      parentWrapper.style.flex = '1';
      parentWrapper.style.width = '100%';
      parentWrapper.style.height = '100%';
      parentWrapper.style.minHeight = '350px';

      // Grandparent (scrolling content div)
      const scrollingContent = parentWrapper.parentElement;
      if (scrollingContent) {
        scrollingContent.style.display = 'flex';
        scrollingContent.style.flexDirection = 'column';
        scrollingContent.style.flex = '1';
        scrollingContent.style.height = 'calc(100% - 30px)'; // Başlık için yer bırak
        scrollingContent.style.minHeight = '350px';

        // Modal dialog container - 3 seviye yukarı
        const modalDialog = scrollingContent.parentElement;
        if (modalDialog) {
          modalDialog.style.display = 'flex';
          modalDialog.style.flexDirection = 'column';
          modalDialog.style.minWidth = '500px';
          modalDialog.style.minHeight = '450px';
        }
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // sonuc değiştiğinde ve viewport'lar hazırsa mesh'leri yükle
    if (changes['sonuc'] && this.viewportsReady && !this.meshesLoaded) {
      this.loadMeshesWhenReady();
    }
  }

  async ngAfterViewInit(): Promise<void> {
    // Ana element için ResizeObserver kur - dialog boyutu değiştiğinde tüm viewport'ları güncelle
    this.setupMainResizeObserver();

    // İki FikoThreeJsComponent'i paralel yükle
    await Promise.all([
      this.loadViewport('A'),
      this.loadViewport('B')
    ]);
  }

  /**
   * Ana element için ResizeObserver kur
   * Dialog boyutu değiştiğinde tüm viewport'ları günceller
   */
  private setupMainResizeObserver(): void {
    const hostElement = this.elementRef.nativeElement as HTMLElement;
    if (!hostElement) return;

    this.resizeObserverMain = new ResizeObserver(() => {
      this.ngZone.runOutsideAngular(() => {
        // Viewport boyutlarını güncelle
        if (this.threeJsA) {
          this.fixRendererSize(this.threeJsA, 'A');
        }
        if (this.threeJsB) {
          this.fixRendererSize(this.threeJsB, 'B');
        }
      });
    });

    this.resizeObserverMain.observe(hostElement);

    // Parent'ı da izle (dialog container)
    const parent = hostElement.parentElement;
    if (parent) {
      this.resizeObserverMain.observe(parent);
    }
  }

  /**
   * Viewport'lar hazır olduğunda mesh'leri yükle
   */
  private loadMeshesWhenReady(): void {
    // Tüm veriler hazır mı kontrol et
    if (!this.sonuc || this.meshesLoaded) return;

    if (this.sahneA && this.sonuc.meshA) {
      this.addDiffMesh(this.sahneA, 'A');
      this.threeJsA?.ciz?.();
    }
    if (this.sahneB && this.sonuc.meshB) {
      this.addDiffMesh(this.sahneB, 'B');
      this.threeJsB?.ciz?.();
    }

    this.meshesLoaded = true;

    // Ekrana sığdır ve kameraları senkronize et
    setTimeout(() => {
      this.ekranaSigdir();

      // İkinci bir senkronizasyon - tüm render işlemleri tamamlandıktan sonra
      setTimeout(() => {
        if (this.syncEnabled) {
          // A'nın kamera durumunu al ve B'ye uygula (master-slave yaklaşımı)
          this.forceFullCameraSync();
        }
      }, 50);
    }, 100);
  }

  /**
   * Kameraları tam olarak senkronize et (zorla)
   * Master viewport olarak A kullanılır
   */
  private forceFullCameraSync(): void {
    const kameraA = this.threeJsA?.aktifKamera;
    const kameraB = this.threeJsB?.aktifKamera;
    const kontrolA = this.threeJsA?.aktifKontrol;
    const kontrolB = this.threeJsB?.aktifKontrol;

    if (!kameraA || !kameraB || !kontrolA || !kontrolB) return;

    // Pozisyon kopyala
    kameraB.position.copy(kameraA.position);

    // Quaternion kopyala (bakış yönü)
    kameraB.quaternion.copy(kameraA.quaternion);

    // Up vektörü kopyala
    kameraB.up.copy(kameraA.up);

    // Orthographic kamera için zoom kopyala
    if (kameraA.isOrthographicCamera && kameraB.isOrthographicCamera) {
      kameraB.zoom = kameraA.zoom;
      kameraB.updateProjectionMatrix();
    }

    // OrbitControls target kopyala
    if (kontrolA.target && kontrolB.target) {
      kontrolB.target.copy(kontrolA.target);
    }

    // OrbitControls güncelle
    kontrolB.update?.();

    // Render
    this.threeJsB?.ciz?.();
  }

  ngOnDestroy(): void {
    // ResizeObserver'ları temizle
    this.resizeObserverMain?.disconnect();
    this.resizeObserverA?.disconnect();
    this.resizeObserverB?.disconnect();

    this.componentRefA?.destroy();
    this.componentRefB?.destroy();
  }

  /**
   * Viewport yükle
   */
  private async loadViewport(panel: 'A' | 'B'): Promise<void> {
    try {
      const { FikoThreeJsComponent } = await import('../../fiko-three-js.component');
      const { SahneStepViewer } = await import('../../Fiko3DComponent/sahne-step-viewer/SahneStepViewer');

      if (!FikoThreeJsComponent) return;

      const viewContainerRef = panel === 'A' ? this.viewContainerRefA : this.viewContainerRefB;
      viewContainerRef.clear();

      // Component oluştur
      const componentRef = viewContainerRef.createComponent(FikoThreeJsComponent, {
        environmentInjector: this.environmentInjector,
        injector: this.injector
      });

      // Referans kaydet
      if (panel === 'A') {
        this.componentRefA = componentRef;
      } else {
        this.componentRefB = componentRef;
      }

      // Input'ları ayarla
      const instance = componentRef.instance;
      if (instance) {
        instance.antiAlias = false;
        instance.enableShadows = false;
        instance.maxFPS = 60;
        instance.pixelRatio = 1;
        instance.enablePostProcessing = false;
        instance.enableFrustumCulling = true;
        instance.enableLOD = false;
      }

      // Host element boyutu - %100 dolduracak şekilde
      const hostElement = componentRef.location.nativeElement as HTMLElement;
      if (hostElement) {
        hostElement.style.width = '100%';
        hostElement.style.height = '100%';
        hostElement.style.display = 'block';
        hostElement.style.position = 'absolute';
        hostElement.style.top = '0';
        hostElement.style.left = '0';
      }

      componentRef.changeDetectorRef.detectChanges();

      // Sahne initialize
      this.initScene(instance, SahneStepViewer, panel);
    } catch (error) {
      console.error(`[FikoCadDiffDialog] Viewport ${panel} yüklenemedi:`, error);
    }
  }

  /**
   * Sahne initialize
   */
  private initScene(instance: any, SahneClass: any, panel: 'A' | 'B'): void {
    let attempts = 0;
    const maxAttempts = 30;

    const check = () => {
      attempts++;
      if (instance?.canvas?.nativeElement && instance?.sunucu) {
        this.createScene(instance, SahneClass, panel);
      } else if (attempts < maxAttempts) {
        setTimeout(check, 100);
      }
    };

    setTimeout(check, 50);
  }

  /**
   * Sahne oluştur
   */
  private createScene(instance: any, SahneClass: any, panel: 'A' | 'B'): void {
    try {
      const sahne = new SahneClass(instance);
      instance.sahneler.push(sahne);

      if (panel === 'A') {
        this.threeJsA = instance;
        this.sahneA = sahne;
      } else {
        this.threeJsB = instance;
        this.sahneB = sahne;
      }

      // Renderer boyutunu düzelt
      this.fixRendererSize(instance, panel);

      // Işık ekle
      this.addLights(sahne);

      // NOT: Mesh'ler burada değil, onReady'de veya loadMeshesWhenReady'de yüklenecek
      // çünkü sonuc input'u henüz set edilmemiş olabilir

      // Render
      instance.ciz?.();

      // Hazır olmasını bekle
      this.waitForReady(instance, panel);
    } catch (error) {
      console.error(`[FikoCadDiffDialog] Sahne ${panel} oluşturulamadı:`, error);
    }
  }

  /**
   * Işık ekle
   */
  private addLights(sahne: any): void {
    const ambient = new THREE.AmbientLight(0xffffff, 0.6);
    sahne.add(ambient);

    const directional = new THREE.DirectionalLight(0xffffff, 0.8);
    directional.position.set(100, 100, 100);
    sahne.add(directional);

    const directional2 = new THREE.DirectionalLight(0xffffff, 0.4);
    directional2.position.set(-100, -100, -100);
    sahne.add(directional2);
  }

  /**
   * Tüm mesh türlerini sahneye ekle (diff + original)
   */
  private addDiffMesh(sahne: any, panel: 'A' | 'B'): void {
    const defaultMode = panel === 'A' ? this.panelAMode : this.panelBMode;

    // diff_both modunda hem A hem B görünür
    const showDiffA = defaultMode === 'diff_a' || defaultMode === 'diff_both';
    const showDiffB = defaultMode === 'diff_b' || defaultMode === 'diff_both';

    // Diff Mesh A
    this.addMeshToScene(sahne, this.sonuc?.meshA, 'DIFF_MESH_A', 'DIFF_EDGES_A', true, showDiffA);

    // Diff Mesh B
    this.addMeshToScene(sahne, this.sonuc?.meshB, 'DIFF_MESH_B', 'DIFF_EDGES_B', true, showDiffB);

    // Original Model A - ana sahnedeki modelin klonu (materyaller dahil)
    if (this.modelA?.nesne) {
      this.addOriginalModelToScene(sahne, this.modelA.nesne, 'ORIGINAL_GROUP_A', defaultMode === 'original_a');
    }

    // Original Model B - ana sahnedeki modelin klonu (materyaller dahil)
    if (this.modelB?.nesne) {
      this.addOriginalModelToScene(sahne, this.modelB.nesne, 'ORIGINAL_GROUP_B', defaultMode === 'original_b');
    }
  }

  /**
   * Orijinal modeli sahneye ekle (materyaller dahil tam klon)
   */
  private addOriginalModelToScene(sahne: any, sourceModel: THREE.Object3D, groupName: string, visible: boolean): void {
    // Daha önce eklenmiş mi kontrol et
    let existingGroup: THREE.Object3D | null = null;
    sahne.traverse((obj: THREE.Object3D) => {
      if (obj.name === groupName) existingGroup = obj;
    });
    if (existingGroup) return;

    // Modeli tamamen klonla (materyaller dahil)
    const clone = sourceModel.clone(true);
    clone.name = groupName;
    clone.visible = visible;

    // Tüm child mesh'leri görünür yap (ana sahnede gizlenmiş olabilir)
    clone.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.visible = true;
        // Frustum culling kapat (küçük viewport için)
        child.frustumCulled = false;
      }
    });

    sahne.add(clone);
  }

  /**
   * Mesh'i sahneye ekle
   */
  private addMeshToScene(
    sahne: any,
    geometry: THREE.BufferGeometry | undefined,
    meshName: string,
    edgeName: string,
    useVertexColors: boolean,
    visible: boolean
  ): void {
    if (!geometry) return;

    // Daha önce eklenmiş mi kontrol et
    let existingMesh: THREE.Object3D | null = null;
    sahne.traverse((obj: THREE.Object3D) => {
      if (obj.name === meshName) existingMesh = obj;
    });
    if (existingMesh) return;

    // Material
    const material = useVertexColors
      ? new THREE.MeshStandardMaterial({
          vertexColors: true,
          side: THREE.DoubleSide,
          roughness: 0.55,
          metalness: 0.15,
          polygonOffset: true,
          polygonOffsetFactor: 4,
          polygonOffsetUnits: 4,
          envMapIntensity: 0.6
        })
      : new THREE.MeshStandardMaterial({
          color: 0x888888,
          side: THREE.DoubleSide,
          roughness: 0.55,
          metalness: 0.15,
          polygonOffset: true,
          polygonOffsetFactor: 4,
          polygonOffsetUnits: 4,
          envMapIntensity: 0.6
        });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = meshName;
    mesh.visible = visible;
    sahne.add(mesh);

    // Edge'leri ekle
    if (this.showEdges) {
      const edgeGeo = new THREE.EdgesGeometry(geometry, 30);
      const edgeMat = new THREE.LineBasicMaterial({
        color: new THREE.Color(this.edgeColor.r, this.edgeColor.g, this.edgeColor.b),
        depthTest: true,
        depthWrite: false
      });
      const edges = new THREE.LineSegments(edgeGeo, edgeMat);
      edges.name = edgeName;
      edges.renderOrder = 1;
      edges.visible = visible;
      sahne.add(edges);
    }
  }

  /**
   * Object3D'den geometry çıkar (merged)
   */
  private extractGeometryFromObject(obj: THREE.Object3D): THREE.BufferGeometry | null {
    if (!obj) return null;

    const geometries: THREE.BufferGeometry[] = [];

    obj.traverse((child) => {
      if (child instanceof THREE.Mesh && child.geometry) {
        try {
          // Geometry'yi clone et
          const geo = child.geometry.clone();

          // Position attribute kontrolü
          if (!geo.attributes.position) return;

          // World matrix güncelle ve uygula
          child.updateWorldMatrix(true, false);

          // Matrix'i oluştur (parent chain dahil)
          const worldMatrix = new THREE.Matrix4();
          worldMatrix.copy(child.matrixWorld);

          geo.applyMatrix4(worldMatrix);
          geometries.push(geo);
        } catch (e) {
          // Clone başarısız olursa atla
        }
      }
    });

    if (geometries.length === 0) return null;
    if (geometries.length === 1) return geometries[0];

    // Birleştir
    try {
      return mergeGeometries(geometries, false);
    } catch (e) {
      // Birleştirme başarısız olursa ilk geometry'yi döndür
      return geometries[0];
    }
  }

  /**
   * Hazır olmasını bekle
   */
  private waitForReady(instance: any, panel: 'A' | 'B'): void {
    let attempts = 0;
    const maxAttempts = 50;

    const check = () => {
      attempts++;
      if (instance?.sahneler?.[0]) {
        this.onReady(instance, panel);
      } else if (attempts < maxAttempts) {
        setTimeout(check, 100);
      }
    };

    setTimeout(check, 200);
  }

  /**
   * Viewport hazır
   */
  private onReady(instance: any, panel: 'A' | 'B'): void {
    this.fixRendererSize(instance, panel);

    // ResizeObserver kur - boyut değişikliklerini dinle
    this.setupResizeObserver(panel);

    if (panel === 'A') {
      this.hazirA = true;
    } else {
      this.hazirB = true;
    }

    // İkisi de hazır olduğunda
    if (this.hazirA && this.hazirB) {
      this.viewportsReady = true;
      this.setupCameraSync();

      // Mesh'ler henüz yüklenmediyse ve sonuc varsa yükle
      if (this.sonuc && !this.meshesLoaded) {
        this.loadMeshesWhenReady();
      } else {
        this.ekranaSigdir();
      }
    }

    this.cd.detectChanges();
  }

  /**
   * Renderer boyutunu container'a göre ayarla
   */
  private fixRendererSize(instance: any, panel?: 'A' | 'B'): void {
    // Container'dan gerçek boyutu al
    const containerRef = panel === 'A' ? this.viewerContainerARef : this.viewerContainerBRef;
    const container = containerRef?.nativeElement;

    let width = 200;
    let height = 150;

    if (container) {
      const rect = container.getBoundingClientRect();
      width = Math.floor(rect.width) || 200;
      height = Math.floor(rect.height) || 150;
    }

    if (instance.canvas?.nativeElement) {
      const canvas = instance.canvas.nativeElement as HTMLCanvasElement;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      canvas.width = width;
      canvas.height = height;
    }

    instance.sunucu?.setSize?.(width, height, false);

    if (instance.aktifKamera?.isOrthographicCamera) {
      instance.aktifKamera.left = width / -2;
      instance.aktifKamera.right = width / 2;
      instance.aktifKamera.top = height / 2;
      instance.aktifKamera.bottom = height / -2;
      instance.aktifKamera.updateProjectionMatrix();
    }

    instance.aktifKontrol?.handleResize?.();
    instance.ciz?.();
  }

  /**
   * ResizeObserver kur - container boyutu değiştiğinde renderer'ı güncelle
   */
  private setupResizeObserver(panel: 'A' | 'B'): void {
    const containerRef = panel === 'A' ? this.viewerContainerARef : this.viewerContainerBRef;
    const instance = panel === 'A' ? this.threeJsA : this.threeJsB;
    const container = containerRef?.nativeElement;

    if (!container || !instance) return;

    const observer = new ResizeObserver(() => {
      this.ngZone.runOutsideAngular(() => {
        this.fixRendererSize(instance, panel);
      });
    });

    observer.observe(container);

    if (panel === 'A') {
      this.resizeObserverA = observer;
    } else {
      this.resizeObserverB = observer;
    }
  }

  /**
   * Kamera senkronizasyonunu kur
   */
  private setupCameraSync(): void {
    const kontrolA = this.threeJsA?.aktifKontrol;
    const kontrolB = this.threeJsB?.aktifKontrol;

    if (!kontrolA || !kontrolB) return;

    kontrolA.addEventListener?.('change', () => {
      if (this.syncEnabled && !this.isSyncing) {
        this.isSyncing = true;
        this.syncCamera(this.threeJsA, this.threeJsB);
        this.isSyncing = false;
      }
    });

    kontrolB.addEventListener?.('change', () => {
      if (this.syncEnabled && !this.isSyncing) {
        this.isSyncing = true;
        this.syncCamera(this.threeJsB, this.threeJsA);
        this.isSyncing = false;
      }
    });
  }

  /**
   * Kamerayı senkronize et
   */
  private syncCamera(source: any, target: any): void {
    const sourceKamera = source?.aktifKamera;
    const sourceKontrol = source?.aktifKontrol;
    const targetKamera = target?.aktifKamera;
    const targetKontrol = target?.aktifKontrol;

    if (!sourceKamera || !targetKamera || !sourceKontrol || !targetKontrol) return;

    // Pozisyon kopyala
    targetKamera.position.copy(sourceKamera.position);

    // Quaternion kopyala (bakış yönü)
    targetKamera.quaternion.copy(sourceKamera.quaternion);

    // Up vektörü kopyala (önemli - döndürme eksenini belirler)
    targetKamera.up.copy(sourceKamera.up);

    // Orthographic kamera için zoom kopyala
    if (sourceKamera.isOrthographicCamera && targetKamera.isOrthographicCamera) {
      targetKamera.zoom = sourceKamera.zoom;
      targetKamera.updateProjectionMatrix();
    }

    // OrbitControls target kopyala
    if (sourceKontrol.target && targetKontrol.target) {
      targetKontrol.target.copy(sourceKontrol.target);
    }

    // OrbitControls güncelle
    targetKontrol.update?.();
    target.ciz?.();
  }

  /**
   * Ekrana sığdır - Her iki viewport da AYNI konuma sığdırılır
   */
  ekranaSigdir(): void {
    // Her iki mesh'i kapsayan ortak bounding box hesapla
    const combinedBox = new THREE.Box3();
    let hasMesh = false;

    // Sahne A'dan mesh'leri ekle
    if (this.sahneA) {
      this.sahneA.traverse((obj: any) => {
        if (obj instanceof THREE.Mesh && obj.geometry) {
          combinedBox.expandByObject(obj);
          hasMesh = true;
        }
      });
    }

    // Sahne B'den mesh'leri ekle
    if (this.sahneB) {
      this.sahneB.traverse((obj: any) => {
        if (obj instanceof THREE.Mesh && obj.geometry) {
          combinedBox.expandByObject(obj);
          hasMesh = true;
        }
      });
    }

    if (!hasMesh) return;

    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    combinedBox.getCenter(center);
    combinedBox.getSize(size);

    const maxDim = Math.max(size.x, size.y, size.z);
    if (maxDim === 0) return;

    // Her iki viewport'u da AYNI konuma sığdır
    this.fitViewportToBox(this.threeJsA, center, maxDim);
    this.fitViewportToBox(this.threeJsB, center, maxDim);
  }

  /**
   * Viewport'u belirtilen merkez ve boyuta sığdır
   */
  private fitViewportToBox(threeJs: any, center: THREE.Vector3, maxDim: number): void {
    if (!threeJs) return;

    const kamera = threeJs.aktifKamera;
    const kontrol = threeJs.aktifKontrol;

    if (!kamera || !kontrol) return;

    // Önce OrbitControls target'ını ayarla (kamera pozisyonundan ÖNCE)
    if (kontrol.target) {
      kontrol.target.set(center.x, center.y, center.z);
    }

    // Kamera pozisyonunu ayarla - isometrik görünüm
    const distance = maxDim * 2;
    const cameraPos = new THREE.Vector3(
      center.x + distance,
      center.y + distance,
      center.z + distance
    );
    kamera.position.copy(cameraPos);

    // Kamerayı merkeze baktır (önemli - bakış yönünü ayarlar)
    kamera.lookAt(center);

    if (kamera.isOrthographicCamera) {
      const viewWidth = kamera.right - kamera.left;
      const viewHeight = kamera.top - kamera.bottom;
      const zoomX = viewWidth / (maxDim * 1.5);
      const zoomY = viewHeight / (maxDim * 1.5);
      kamera.zoom = Math.min(zoomX, zoomY);
      kamera.updateProjectionMatrix();
    }

    // OrbitControls'u güncelle (pozisyon ve target ayarlandıktan sonra)
    kontrol.update?.();
    threeJs.ciz?.();
  }

  kameraSifirla(): void {
    this.ekranaSigdir();
  }

  toggleSync(): void {
    this.syncEnabled = !this.syncEnabled;
    this.cd.detectChanges();
  }

  /**
   * Panel görünüm modu değiştiğinde
   */
  onPanelModeChange(panel: 'A' | 'B'): void {
    const mode = panel === 'A' ? this.panelAMode : this.panelBMode;
    const sahne = panel === 'A' ? this.sahneA : this.sahneB;
    const threeJs = panel === 'A' ? this.threeJsA : this.threeJsB;

    if (!sahne) return;

    // Tüm mesh'leri gizle
    this.hideAllMeshesInScene(sahne);

    // Seçilen moda göre görünürlüğü ayarla
    switch (mode) {
      case 'diff_a':
        this.showMeshByName(sahne, 'DIFF_MESH_A', true);
        this.showMeshByName(sahne, 'DIFF_EDGES_A', true);
        break;
      case 'diff_b':
        this.showMeshByName(sahne, 'DIFF_MESH_B', true);
        this.showMeshByName(sahne, 'DIFF_EDGES_B', true);
        break;
      case 'diff_both':
        // Hem eklenen hem silinen - ikisini birden göster
        this.showMeshByName(sahne, 'DIFF_MESH_A', true);
        this.showMeshByName(sahne, 'DIFF_EDGES_A', true);
        this.showMeshByName(sahne, 'DIFF_MESH_B', true);
        this.showMeshByName(sahne, 'DIFF_EDGES_B', true);
        break;
      case 'original_a':
        this.showMeshByName(sahne, 'ORIGINAL_GROUP_A', true);
        break;
      case 'original_b':
        this.showMeshByName(sahne, 'ORIGINAL_GROUP_B', true);
        break;
    }

    threeJs?.ciz?.();
    this.cd.detectChanges();
  }

  /**
   * Sahnedeki tüm diff/original mesh'leri gizle
   */
  private hideAllMeshesInScene(sahne: any): void {
    const meshNames = [
      'DIFF_MESH_A', 'DIFF_EDGES_A',
      'DIFF_MESH_B', 'DIFF_EDGES_B',
      'ORIGINAL_GROUP_A', 'ORIGINAL_GROUP_B'
    ];

    sahne.traverse((obj: THREE.Object3D) => {
      if (meshNames.includes(obj.name)) {
        obj.visible = false;
      }
    });
  }

  /**
   * İsme göre mesh görünürlüğünü ayarla
   */
  private showMeshByName(sahne: any, name: string, visible: boolean): void {
    sahne.traverse((obj: THREE.Object3D) => {
      if (obj.name === name) {
        obj.visible = visible;
      }
    });
  }

  formatSayi(sayi: number): string {
    if (sayi >= 1000000) return (sayi / 1000000).toFixed(1) + 'M';
    if (sayi >= 1000) return (sayi / 1000).toFixed(1) + 'K';
    return sayi.toString();
  }

  formatSayiDec(sayi: number, decimals: number = 2): string {
    if (sayi >= 1000000) return (sayi / 1000000).toFixed(1) + 'M';
    if (sayi >= 1000) return (sayi / 1000).toFixed(1) + 'K';
    return sayi.toFixed(decimals);
  }

  formatMesafe(mm: number): string {
    if (mm <= 0) return '0 mm';
    if (mm >= 1000) return (mm / 1000).toFixed(2) + ' m';
    if (mm >= 10) return mm.toFixed(1) + ' mm';
    if (mm >= 1) return mm.toFixed(2) + ' mm';
    if (mm >= 0.01) return mm.toFixed(3) + ' mm';
    return mm.toExponential(1) + ' mm';
  }

  rgbToHex(rgb: RGB): string {
    const toHex = (c: number) => {
      const hex = Math.round(c * 255).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return '#' + toHex(rgb.r) + toHex(rgb.g) + toHex(rgb.b);
  }
}
